import { useContext, useState } from "react";
import { Box, Typography, Button, Avatar, CircularProgress } from "@mui/material";
import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import HomeRepairServiceOutlinedIcon from "@mui/icons-material/HomeRepairServiceOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";

import { AppContext } from "../../Context/AppContext";
import CustomTooltip from "../../Components/CustomTooltip";
import CustomFilterTabs from "../../Components/CustomFilterTabs";
// Reuse the presentational pieces built for the Community Manager dashboard —
// same visual language, no duplication.
import StatCard from "../Dashboard/components/StatCard";
import TopFiveTable from "../Dashboard/components/TopFiveTable";
import SetupCard from "../Dashboard/components/SetupCard";
import Sparkline from "../Dashboard/components/Sparkline";
import HealthStat from "../Dashboard/components/HealthStat";
import { useSupplierDashboard } from "./hooks/useSupplierDashboard";
import {
  dashboardStyles as d,
  statCardStyles as sc,
  topFiveStyles as tf,
} from "../Dashboard/styles/styles";

const fmt = (n) => Number(n).toLocaleString("en-US");
const listTitleIcon = { fontSize: 18, color: "rgba(47, 43, 61, 0.55)" };
const loadingBoxSx = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "60vh",
};

const CARD_BORDER = "1px solid rgba(234, 234, 234, 1)";
const AMBER = { color: "rgba(180, 120, 20, 1)", bg: "rgba(255, 244, 229, 1)" };
const GREEN = { color: "rgba(36, 139, 112, 1)", bg: "rgba(219, 240, 236, 1)" };
const NEUTRAL = { color: "rgba(62, 63, 76, 0.7)", bg: "rgba(242, 243, 251, 1)" };

const TONES = {
  good: { color: "rgba(36, 139, 112, 1)", bg: "rgba(219, 240, 236, 1)" },
  warn: { color: "rgba(180, 120, 20, 1)", bg: "rgba(255, 244, 229, 1)" },
  bad: { color: "rgba(200, 55, 55, 1)", bg: "rgba(252, 235, 235, 1)" },
  neutral: { color: "rgba(62, 63, 76, 0.7)", bg: "rgba(242, 243, 251, 1)" },
};
const JOB_TONES = {
  awaiting_approval: AMBER,
  in_progress: GREEN,
  scheduled: NEUTRAL,
};
const tier = (value, good, warn, higherBetter) => {
  // No data earns no colour — a missing metric is not a bad one.
  if (value === null || value === undefined) return "neutral";
  if (higherBetter) return value >= good ? "good" : value >= warn ? "warn" : "bad";
  return value <= good ? "good" : value <= warn ? "warn" : "bad";
};

// Local styles for the supplier-only sections (action tiles + communities).
const a = {
  tileGrid: {
    display: "grid",
    gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(3, minmax(0, 1fr))" },
    gap: "12px",
  },
  tile: {
    border: CARD_BORDER,
    borderRadius: "12px",
    backgroundColor: "#fff",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    height: "100%",
  },
  tileLabelRow: { display: "flex", alignItems: "center", gap: "6px" },
  tileLabel: { fontSize: "13px", fontWeight: 400, color: "rgba(71, 84, 103, 1)" },
  tileValue: { fontSize: "30px", fontWeight: 600, lineHeight: "120%" },
  tileLink: {
    fontSize: "13px",
    fontWeight: 500,
    color: "rgba(36, 139, 112, 1)",
    textDecoration: "underline",
    cursor: "pointer",
    marginTop: "6px",
  },
  chip: {
    display: "inline-block",
    fontSize: "12px",
    fontWeight: 500,
    padding: "2px 10px",
    borderRadius: "10px",
    whiteSpace: "nowrap",
  },
  commCard: {
    border: CARD_BORDER,
    borderRadius: "12px",
    backgroundColor: "#fff",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    gap: "14px",
  },
  commTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    flexWrap: "wrap",
  },
  pendingRow: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    paddingTop: "12px",
    borderTop: CARD_BORDER,
  },
  pendingText: { fontSize: "13px", color: "rgba(62, 63, 76, 0.9)", minWidth: 0 },
  findBtn: {
    textTransform: "none",
    fontSize: "13px",
    fontWeight: 500,
    color: "rgba(36, 139, 112, 1)",
    border: "1px solid rgba(36, 139, 112, 0.5)",
    borderRadius: "8px",
    padding: "5px 14px",
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "rgba(36, 139, 112, 0.06)", border: "1px solid rgba(36, 139, 112, 0.5)" },
  },
  joinBtn: {
    textTransform: "none",
    fontSize: "12px",
    fontWeight: 500,
    color: "#fff",
    backgroundColor: "rgba(36, 139, 112, 1)",
    borderRadius: "8px",
    padding: "3px 14px",
    boxShadow: "none",
    whiteSpace: "nowrap",
    "&:hover": { backgroundColor: "rgba(30, 118, 95, 1)", boxShadow: "none" },
  },
};

const SupplierDashboardContainer = () => {
  const { t, i18n } = useTranslation("page-supplier-dashboard");
  const { sidebarData } = useContext(AppContext);
  const lang = (i18n.language || "en").toLowerCase().startsWith("he") ? "he" : "en";
  const loc = (v) =>
    v && typeof v === "object" && !Array.isArray(v) ? v[lang] ?? v.en : v;
  const locPoints = (pts) =>
    pts.map((p) => ({ value: p.value, label: loc(p.label), full: loc(p.full) }));
  const supplierName = sidebarData?.supplierName || "";
  const supplierImage = sidebarData?.supplierImage || "";
  const { data: m, isLoading, error } = useSupplierDashboard();
  // Every hook must run before the loading/error return below, so this one
  // takes a static default and `activeType` reconciles it against the tabs the
  // supplier actually has once the data lands.
  const [qualityType, setQualityType] = useState("services");

  if (isLoading || (!m && !error)) {
    return (
      <Box sx={{ ...d.page, ...loadingBoxSx }}>
        <CircularProgress sx={{ color: "rgba(36, 139, 112, 1)" }} />
      </Box>
    );
  }

  if (error || !m) {
    return (
      <Box sx={{ ...d.page, ...loadingBoxSx }}>
        <Typography sx={{ color: "rgba(62, 63, 76, 0.7)" }}>
          {t("load_error")}
        </Typography>
      </Box>
    );
  }

  const cur = m.currencySymbol || "₪";
  const money = (n) => `${cur}${fmt(n)}`;

  // Which offering types this supplier actually has drives the adaptive areas.
  const qualityTabs = [
    m.hasServices ? "services" : null,
    m.hasProducts ? "products" : null,
  ].filter(Boolean);
  const activeType = qualityTabs.includes(qualityType)
    ? qualityType
    : qualityTabs[0] || "services";

  // A supplier with no orders yet divides by zero — show 0, not NaN.
  const avg = (total, count) => (count ? Math.round(total / count) : 0);
  const avgAll = avg(m.sales.moneyAllTime, m.sales.countAllTime);
  const avgMonth = avg(m.sales.moneyThisMonth, m.sales.countThisMonth);

  const infoTip = (text) => (
    <CustomTooltip title={text}>
      <Box component="span" sx={sc.tooltipTrigger}>
        <Box
          component="img"
          src="/icons/question-circle-icon.svg"
          alt=""
          sx={sc.tooltipIcon}
        />
      </Box>
    </CustomTooltip>
  );

  const sectionHeader = (title, tip) => (
    <Box sx={d.sectionHeader}>
      <Typography sx={d.sectionTitle}>{title}</Typography>
      {tip ? infoTip(tip) : null}
    </Box>
  );

  // Top-list name cell: rank + thumbnail + name.
  const nameCell = (item, index) => (
    <Box sx={tf.nameCell}>
      <Box component="span" sx={tf.rank}>
        {index + 1}
      </Box>
      <Box sx={tf.avatar}>
        {item.image ? (
          <Box component="img" src={item.image} alt="" sx={tf.avatarImg} />
        ) : (
          loc(item.name).charAt(0)
        )}
      </Box>
      <span>{loc(item.name)}</span>
    </Box>
  );

  // Job cell: thumbnail + name, no rank (a queue, not a ranking).
  const mediaCell = (item) => (
    <Box sx={tf.nameCell}>
      <Box sx={tf.avatar}>
        {item.image ? (
          <Box component="img" src={item.image} alt="" sx={tf.avatarImg} />
        ) : (
          loc(item.name).charAt(0)
        )}
      </Box>
      <span>{loc(item.name)}</span>
    </Box>
  );

  const toned = (text, level) => (
    <Box component="span" sx={{ color: TONES[level].color, fontWeight: 500 }}>
      {text}
    </Box>
  );

  const statusChip = (status) => (
    <Box
      component="span"
      sx={{ ...a.chip, color: JOB_TONES[status].color, backgroundColor: JOB_TONES[status].bg }}
    >
      {t(`jobStatus.${status}`)}
    </Box>
  );

  // ---- Needs-action layer ------------------------------------------------
  const actionTiles = [
    {
      key: "pending",
      value: m.actions.pendingApproval,
      label: t("action.pending"),
      tip: t("action.pending_tip"),
      accent: m.actions.pendingApproval > 0 ? AMBER.color : "rgba(47, 43, 61, 0.9)",
      showReview: m.actions.pendingApproval > 0,
    },
    {
      key: "in_progress",
      value: m.actions.inProgress,
      label: t("action.in_progress"),
      accent: "#7367F0",
    },
    {
      key: "due_today",
      value: m.actions.dueToday,
      label: t("action.due_today"),
      accent: GREEN.color,
    },
    // Overdue is its own tile, and only when there is something to show. It is
    // deliberately not folded into "due today": a job that slipped last month
    // is a different problem from one due in a few hours, and merging them
    // makes both numbers meaningless.
    ...(m.actions.overdue > 0
      ? [
          {
            key: "overdue",
            value: m.actions.overdue,
            label: t("action.overdue"),
            tip: t("action.overdue_tip"),
            accent: TONES.bad.color,
            showReview: true,
          },
        ]
      : []),
  ];

  const jobColumns = [
    { label: t("cols.job"), render: (row) => mediaCell(row) },
    { label: t("cols.community"), render: (row) => <span>{loc(row.community)}</span> },
    { label: t("cols.when"), render: (row) => <Box component="span" sx={tf.count}>{loc(row.when)}</Box> },
    { label: t("cols.status"), align: "right", render: (row) => statusChip(row.status) },
  ];

  // ---- Top performers (only lists the supplier has content for) ----------
  const ordersCol = {
    label: t("cols.orders"),
    align: "right",
    render: (row) => (
      <Box component="span" sx={tf.count}>
        {fmt(row.count)}
      </Box>
    ),
  };
  const revenueCol = {
    label: t("cols.revenue"),
    align: "right",
    render: (row) => (
      <Box component="span" sx={tf.money}>
        {money(row.revenue)}
      </Box>
    ),
  };

  const topTables = [
    m.hasServices && m.topServices.length
      ? {
          key: "services",
          icon: <HomeRepairServiceOutlinedIcon sx={listTitleIcon} />,
          title: t("top.services"),
          tip: t("top.tips.services"),
          rows: m.topServices,
          columns: [
            { label: t("cols.service"), render: (row, i) => nameCell(row, i) },
            ordersCol,
            revenueCol,
          ],
        }
      : null,
    m.hasProducts && m.topProducts.length
      ? {
          key: "products",
          icon: <Inventory2OutlinedIcon sx={listTitleIcon} />,
          title: t("top.products"),
          tip: t("top.tips.products"),
          rows: m.topProducts,
          columns: [
            { label: t("cols.product"), render: (row, i) => nameCell(row, i) },
            ordersCol,
            revenueCol,
          ],
        }
      : null,
    m.topCommunities.length
      ? {
          key: "communities",
          icon: <StorefrontOutlinedIcon sx={listTitleIcon} />,
          title: t("top.communities"),
          tip: t("top.tips.communities"),
          rows: m.topCommunities,
          columns: [
            { label: t("cols.community"), render: (row, i) => nameCell(row, i) },
            revenueCol,
          ],
        }
      : null,
  ].filter(Boolean);

  const topGridSx = {
    ...d.topGrid,
    gridTemplateColumns: {
      xs: "minmax(0, 1fr)",
      md: `repeat(${topTables.length}, minmax(0, 1fr))`,
    },
  };

  // ---- Order quality -----------------------------------------------------
  const isServices = activeType === "services";
  const q = m.quality[activeType];
  // A metric is null when the supplier has no orders in that window — render a
  // dash rather than a confident "0%", which would read as a failing score.
  const NO_DATA = "—";
  const fmtUnit = (v, unit) =>
    v === null || v === undefined
      ? NO_DATA
      : unit === "hours"
        ? `${v} ${t("sq.hours")}`
        : unit === "days"
          ? `${v} ${t("sq.days")}`
          : `${v}%`;
  const metricConfig = [
    { key: "fulfillment", unit: "percent", labelKey: "sq.fulfillment_rate", tipKey: "sq.tips.fulfillment", good: 90, warn: 75, higherBetter: true },
    { key: "approval", unit: "hours", labelKey: isServices ? "sq.avg_response" : "sq.avg_approval", tipKey: isServices ? "sq.tips.response" : "sq.tips.approval", good: 4, warn: 12, higherBetter: false },
    { key: "completion", unit: "days", labelKey: isServices ? "sq.avg_completion" : "sq.avg_delivery", tipKey: isServices ? "sq.tips.completion" : "sq.tips.delivery", good: 2, warn: 4, higherBetter: false },
    { key: "cancellation", unit: "percent", labelKey: "sq.cancellation_rate", tipKey: "sq.tips.cancellation", good: 5, warn: 12, higherBetter: false },
  ];
  const healthMetrics = metricConfig.map((c) => {
    const md = q.metrics[c.key];
    return {
      label: t(c.labelKey),
      value: fmtUnit(md.raw, c.unit),
      level: tier(md.raw, c.good, c.warn, c.higherBetter),
      tip: t(c.tipKey),
      trend: md.trend.map(([month, v]) => [loc(month), fmtUnit(v, c.unit)]),
    };
  });
  const qualityColumns = [
    { label: t("cols.community"), render: (row, i) => nameCell(row, i) },
    { label: t("cols.fulfillment"), align: "right", render: (row) => toned(`${row.fulfillment}%`, tier(row.fulfillment, 90, 75, true)) },
    { label: t(isServices ? "cols.response" : "cols.approval"), align: "right", render: (row) => (
      <Box component="span" sx={tf.count}>
        {row.approvalHours === null || row.approvalHours === undefined
          ? NO_DATA
          : `${row.approvalHours} ${t("sq.hours")}`}
      </Box>
    ) },
    { label: t("cols.cancellations"), align: "right", render: (row) => toned(`${row.cancellationRate}%`, tier(row.cancellationRate, 5, 12, false)) },
  ];

  return (
    <Box sx={d.page}>
      <Box sx={d.header}>
        <Avatar src={supplierImage} alt={supplierName} sx={d.headerLogo}>
          {supplierName.charAt(0)}
        </Avatar>
        <Typography sx={d.title}>
          {supplierName
            ? t("page_title", { name: supplierName })
            : t("page_title_fallback")}
        </Typography>
      </Box>

      {/* --- Needs you now: the operational layer, leading the dashboard --- */}
      {sectionHeader(t("sections.action"))}
      <Box
        sx={{
          ...a.tileGrid,
          // 3 tiles normally, 4 when the supplier has overdue work.
          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            sm: `repeat(${actionTiles.length}, minmax(0, 1fr))`,
          },
        }}
      >
        {actionTiles.map((tile) => (
          <Box key={tile.key} sx={a.tile}>
            <Box sx={a.tileLabelRow}>
              <Typography sx={a.tileLabel}>{tile.label}</Typography>
              {tile.tip ? infoTip(tile.tip) : null}
            </Box>
            <Typography sx={{ ...a.tileValue, color: tile.accent }}>
              {fmt(tile.value)}
            </Typography>
            {tile.showReview && (
              <Typography component={NavLink} to="/my-jobs" sx={a.tileLink}>
                {t("action.review")}
              </Typography>
            )}
          </Box>
        ))}
      </Box>
      <TopFiveTable
        emptyText={t("sq.no_data")}
        icon={<WorkOutlineOutlinedIcon sx={listTitleIcon} />}
        title={t("action.upcoming_title")}
        tooltip={infoTip(t("action.upcoming_tip"))}
        columns={jobColumns}
        rows={m.upcomingJobs}
      />

      {/* --- Money --- */}
      {sectionHeader(t("sections.money"))}
      <Box sx={d.chartGrid}>
        <StatCard
          icon={<ShoppingBagOutlinedIcon sx={{ color: "#7367F0", fontSize: 24 }} />}
          iconBg="rgba(242, 243, 251, 1)"
          label={t("cards.sales")}
          tooltip={infoTip(t("tooltips.sales"))}
          hideIconOnMobile
        >
          <Typography sx={sc.amount}>{money(m.sales.moneyAllTime)}</Typography>
          <Typography sx={sc.subline}>
            {t("sales_alltime", {
              count: fmt(m.sales.countAllTime),
              avg: money(avgAll),
            })}
          </Typography>
          <Typography sx={sc.subline}>
            {t("sales_month", {
              money: money(m.sales.moneyThisMonth),
              count: fmt(m.sales.countThisMonth),
              avg: money(avgMonth),
            })}
          </Typography>
          <Sparkline
            points={locPoints(m.sales.monthly)}
            color="#7367F0"
            fill="rgba(115, 103, 240, 0.12)"
            formatValue={money}
          />
        </StatCard>

        <StatCard
          icon={
            <PaymentsOutlinedIcon
              sx={{ color: "rgba(36, 139, 112, 1)", fontSize: 24 }}
            />
          }
          iconBg="rgba(219, 240, 236, 1)"
          label={t("cards.earnings")}
          tooltip={infoTip(t("tooltips.earnings"))}
          hideIconOnMobile
          // The platform has no supplier balance or payout mechanism yet, so
          // the API always sends currentBalance: null. The wallet block stays
          // visible on purpose — the Withdraw button is still the placeholder
          // it was in the FE-first build, wired to nothing. Only the amount
          // falls back to a dash, because `money(null)` renders "₪NaN".
          headerRight={
            <Box sx={sc.walletTop}>
              <Box sx={sc.balanceBox}>
                <Typography sx={sc.balanceLabel}>
                  {t("current_balance")}
                </Typography>
                <Typography sx={sc.balanceValue}>
                  {m.earnings.currentBalance === null
                    ? NO_DATA
                    : money(m.earnings.currentBalance)}
                </Typography>
              </Box>
              <Button sx={sc.withdrawBtn} variant="contained" disableElevation>
                {t("withdraw")}
              </Button>
            </Box>
          }
        >
          <Typography sx={sc.amount}>{money(m.earnings.allTime)}</Typography>
          <Typography sx={sc.subline}>{t("earn_alltime")}</Typography>
          <Typography sx={sc.subline}>
            {t("earn_month", { money: money(m.earnings.thisMonth) })}
          </Typography>
          <Sparkline
            points={locPoints(m.earnings.monthly)}
            color="rgba(36, 139, 112, 1)"
            fill="rgba(36, 139, 112, 0.12)"
            formatValue={money}
          />
        </StatCard>
      </Box>

      {/* --- Communities & growth --- */}
      {sectionHeader(t("sections.communities"))}
      <Box sx={a.commCard}>
        <Box sx={a.commTopRow}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <Box
              sx={{
                ...sc.iconBox("rgba(255, 244, 229, 1)"),
                width: "44px",
                height: "44px",
                minWidth: "44px",
              }}
            >
              <GroupsOutlinedIcon sx={{ color: "#ff9f43", fontSize: 22 }} />
            </Box>
            <Box>
              <Typography sx={sc.statNum}>{fmt(m.communities.active)}</Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: "4px" }}>
                <Typography sx={{ fontSize: "13px", color: "rgba(71, 84, 103, 1)" }}>
                  {t("communities.active")}
                </Typography>
                {infoTip(t("communities.active_tip"))}
              </Box>
            </Box>
          </Box>
          <Button component={NavLink} to="/find-communities" sx={a.findBtn}>
            {t("communities.find_cta")}
          </Button>
        </Box>

        {m.communities.pending.map((c, i) => (
          <Box key={i} sx={a.pendingRow}>
            <Box sx={tf.avatar}>
              {c.image ? (
                <Box component="img" src={c.image} alt="" sx={tf.avatarImg} />
              ) : (
                loc(c.name).charAt(0)
              )}
            </Box>
            <Typography sx={a.pendingText}>
              <Box component="span" sx={{ fontWeight: 500 }}>
                {loc(c.name)}
              </Box>{" "}
              {t(c.type === "invite" ? "communities.invite_text" : "communities.request_text")}
              {" · "}
              {t("communities.members", { count: c.members })}
            </Typography>
            <Box sx={{ marginInlineStart: "auto" }}>
              {c.type === "invite" ? (
                // `invite_community_id` puts My Communities on the Join
                // requests tab and pops the offer prompt for THIS community.
                // It also bypasses the once-per-request suppression there, so
                // coming from the dashboard prompts every time the invitation
                // is still unanswered — while simply opening the tab does not.
                <Button
                  component={NavLink}
                  to={`/my-communities?invite_community_id=${c.id}`}
                  sx={a.joinBtn}
                >
                  {t("communities.join")}
                </Button>
              ) : (
                <Box component="span" sx={{ ...a.chip, ...NEUTRAL, color: NEUTRAL.color, backgroundColor: NEUTRAL.bg }}>
                  {t("communities.pending_label")}
                </Box>
              )}
            </Box>
          </Box>
        ))}
      </Box>

      {/* --- Analytics: top performers --- */}
      {topTables.length > 0 && sectionHeader(t("sections.top"))}
      {topTables.length > 0 && (
        <Box sx={topGridSx}>
          {topTables.map((tt) => (
            <TopFiveTable
        emptyText={t("sq.no_data")}
              key={tt.key}
              icon={tt.icon}
              title={tt.title}
              tooltip={infoTip(tt.tip)}
              columns={tt.columns}
              rows={tt.rows}
            />
          ))}
        </Box>
      )}

      {/* --- Analytics: order quality --- */}
      <Box sx={d.qualityHeaderRow}>
        <Box sx={{ display: "flex", alignItems: "center", gap: "6px" }}>
          <Typography sx={d.sectionTitle}>{t("sections.quality")}</Typography>
          {infoTip(t("sections.quality_tip"))}
        </Box>
        {qualityTabs.length > 1 && (
          <Box sx={{ minWidth: 0, maxWidth: "100%" }}>
            <CustomFilterTabs
              tabs={[
                { label: t("sq.tab_services"), value: "services" },
                { label: t("sq.tab_products"), value: "products" },
              ]}
              activeTab={activeType}
              onChange={(_, v) => setQualityType(v)}
            />
          </Box>
        )}
      </Box>
      <Box sx={d.qualityGrid}>
        <Box sx={d.healthGrid}>
          {healthMetrics.map((h, i) => (
            <HealthStat
              key={i}
              label={h.label}
              value={h.value}
              tone={TONES[h.level]}
              // No data earns no pill. `sq.status` only defines good/warn/bad —
              // a "neutral" lookup would render the raw key next to a "—".
              statusText={h.level === "neutral" ? null : t(`sq.status.${h.level}`)}
              tooltip={infoTip(h.tip)}
              trend={h.trend}
            />
          ))}
        </Box>
        <TopFiveTable
        emptyText={t("sq.no_data")}
          icon={<SpeedOutlinedIcon sx={listTitleIcon} />}
          title={t("sq.community_table_title")}
          tooltip={infoTip(t("sq.community_table_tip"))}
          columns={qualityColumns}
          rows={q.communities}
        />
      </Box>

      <SetupCard />
    </Box>
  );
};

export default SupplierDashboardContainer;
